import { useApolloClient, useQuery } from '@apollo/client';
import moment from 'moment';
import React, { useMemo, useState } from 'react';
import Button from 'react-bootstrap/Button';
import Card from 'react-bootstrap/Card';
import Col from 'react-bootstrap/Col';
import Row from 'react-bootstrap/Row';
import { useRouteMatch } from 'react-router-dom';
import { LoadingLogo } from '../../components/Modal';
import {
  GET_SESSIONS,
  GET_SESSIONS_COST_ESTIMATE,
  GET_SESSION_LOGS_COST_ESTIMATE,
  GET_USERS_FROM_SESSION,
} from '../../graphql';
import { InfoDisplay } from './components/InfoDisplay';
import { SessionDisplay } from './components/SessionDisplay';
import { idToTimestamp } from './models';

const isUserID = (id) => id.split('-').length === 5;

const formatBytes = (bytes) => {
  if (bytes >= 1024 ** 3) return `${(bytes / 1024 ** 3).toFixed(bytes >= 10 * 1024 ** 3 ? 0 : 1)} GB`;
  if (bytes >= 1024 ** 2) return `${(bytes / 1024 ** 2).toFixed(0)} MB`;
  return `${bytes} B`;
};

// Each "Load More" reveals one more window of this many days. The first window is fetched up to now;
// every later window is a bounded [start, end] slice, so BigQuery scans only the newly revealed days
// instead of re-scanning startDate..today on every click (the old refetch widened the scan each time).
const WINDOW_DAYS = 7;

export const SessionsView = () => {
  const match = useRouteMatch();
  const { id } = match.params;
  const client = useApolloClient();
  const idParams = isUserID(id) ? { userID: id } : { installID: id };

  // Stable first window ([now - WINDOW_DAYS, now]); computed once so useQuery variables don't change
  // every render (a fresh moment() each render would retrigger the query — and a new BigQuery scan).
  const [firstStart] = useState(() => moment().subtract(WINDOW_DAYS, 'days'));
  const [firstEnd] = useState(() => moment());
  // The oldest window start fetched so far, and the older slices accumulated by Load More.
  const [oldestStart, setOldestStart] = useState(firstStart);
  const [olderLogs, setOlderLogs] = useState([]);
  const [loadingMore, setLoadingMore] = useState(false);

  const { data, loading } = useQuery(GET_SESSIONS, {
    variables: { ...idParams, startDate: firstStart, endDate: firstEnd },
  });
  const { data: args } = useQuery(GET_USERS_FROM_SESSION, {
    variables: { properties: [id], type: 'BY_ID' },
    // prevent cache issue when coming from user view
    fetchPolicy: 'no-cache',
  });

  // The window the next "Load More" would fetch. Memoized so its moment instances are stable across
  // renders (fresh ones each render would keep retriggering the estimate query below).
  const nextWindow = useMemo(
    () => ({
      startDate: moment(oldestStart).subtract(WINDOW_DAYS, 'days'),
      endDate: moment(oldestStart).subtract(1, 'day'),
    }),
    [oldestStart]
  );

  // Dry-run estimate ($0) of that next window, so we can warn how much a click will scan.
  const { data: estData, loading: estLoading } = useQuery(GET_SESSIONS_COST_ESTIMATE, {
    variables: { ...idParams, startDate: nextWindow.startDate, endDate: nextWindow.endDate },
  });
  const estimate = estData?.analytics?.appSessionsCostEstimate;

  const handleLoadMore = async () => {
    setLoadingMore(true);
    try {
      // no-cache so this older slice never overwrites the first window held by useQuery above.
      const res = await client.query({
        query: GET_SESSIONS,
        variables: { ...idParams, startDate: nextWindow.startDate, endDate: nextWindow.endDate },
        fetchPolicy: 'no-cache',
      });
      const slice = res?.data?.analytics?.appSessions?.data ?? [];
      setOlderLogs((prev) => [...prev, ...slice.map((log) => JSON.parse(log))]);
      setOldestStart(nextWindow.startDate);
    } finally {
      setLoadingMore(false);
    }
  };

  let firstLogs = [];
  if (data && data.analytics.appSessions && data.analytics.appSessions.data.length > 0) {
    firstLogs = data.analytics.appSessions.data.map((log) => JSON.parse(log));
  }

  // Merge the first window with the accumulated older slices, deduping by session_id in case a session
  // sits on a window boundary and appears in two adjacent scans.
  const seen = new Set();
  const processedLogs = [...firstLogs, ...olderLogs].filter((log) => {
    const sid = log?.event_params?.session_id;
    if (!sid) {
      return true;
    }
    if (seen.has(sid)) {
      return false;
    }
    seen.add(sid);
    return true;
  });

  // Opening any session ("Load Session") scans mostly a ±1 day partition of the backend table, so the
  // cost is about the same for every session here. Estimate it once from one sample session (dry-run,
  // $0) to warn near Load More what opening a session below will cost.
  const sampleSessionId = processedLogs[0]?.event_params?.session_id;
  const sampleSessionDate = useMemo(
    () => (sampleSessionId ? moment(idToTimestamp(sampleSessionId)) : null),
    [sampleSessionId]
  );
  const { data: sessionEstData, loading: sessionEstLoading } = useQuery(GET_SESSION_LOGS_COST_ESTIMATE, {
    variables: { sessionID: sampleSessionId, sessionDate: sampleSessionDate },
    skip: !sampleSessionId,
  });
  const sessionEstimate = sessionEstData?.analytics?.appLogsBySessionCostEstimate;

  const busy = loading || loadingMore;

  const infoData = [];
  if (processedLogs && isUserID(id)) {
    infoData.push({
      title: 'Players',
      params: args
        ? args.users.map((user) => {
            const {
              id: _id,
              displayName,
              picture,
              subscriptionTier,
              phoneCredential,
              emailCredential,
              facebookCredential,
              appleCredential,
              googleCredential,
              userLevel,
              userLevelProgressToNextLevel,
              coinBalance,
              diamondBalance,
              basicTierContentPack: {
                contentPack: { name },
              },
              ABTestList,
            } = user;
            const account = {
              userId: _id,
              displayName,
              picture,
              subscriptionTier,
              phoneCredential,
              emailCredential,
              facebookCredential,
              appleCredential,
              googleCredential,
            };
            return {
              account,
              user: {
                userLevel,
                userLevelProgressToNextLevel,
                coinBalance,
                diamondBalance,
                basicTierContentPack: name,
              },
              abtests: ABTestList.map(({ id: abtestid, variation }) => ({ [abtestid]: variation })),
            };
          })
        : [{ error: 'No user found' }],
    });
  }

  return (
    <>
      <Row>
        <Col sm="8">
          <Card>
            <Card.Header className="m-0 font-weight-bold text-primary">
              {`Recent Sessions (last ${moment().diff(moment(oldestStart), 'days')} days)`}
            </Card.Header>
            <Card.Body>
              {processedLogs && processedLogs.length > 0
                ? processedLogs
                    .sort((a, b) => b.event_timestamp - a.event_timestamp)
                    .map(({ event_params: params }) => <SessionDisplay key={params.session_id} session={params} />)
                : !busy && <p>No log found, try loading more</p>}
              {estimate ? (
                <small className="text-warning d-block mb-1">
                  ⚠️ <strong>“Load More” is billed each click</strong> — it scans the next {WINDOW_DAYS} days in
                  BigQuery (~{formatBytes(estimate.bytes)} ≈ ${estimate.estimatedUsd.toFixed(2)}). Sessions already
                  shown stay in memory and are not re-scanned; <strong>only the new days cost</strong>. Click only
                  when you need older sessions.
                </small>
              ) : (
                estLoading && <small className="text-muted d-block mb-1">⏳ Estimating “Load More” BigQuery cost…</small>
              )}
              {sessionEstimate ? (
                <small className="text-muted d-block mb-1">
                  Each <strong>“Load Session”</strong> above opens a detail view that scans ~
                  {formatBytes(sessionEstimate.bytes)} (≈ ${sessionEstimate.estimatedUsd.toFixed(2)}) in BigQuery,
                  mostly backend logs. Open only the sessions you need.
                </small>
              ) : (
                sampleSessionId &&
                sessionEstLoading && (
                  <small className="text-muted d-block mb-1">⏳ Estimating “Load Session” BigQuery cost…</small>
                )
              )}
              <div className="d-flex align-content-center">
                <Button onClick={handleLoadMore} disabled={busy}>
                  Load More
                </Button>
                <LoadingLogo className="mx-2" show={busy} />
              </div>
            </Card.Body>
          </Card>
        </Col>
        <InfoDisplay data={infoData} />
      </Row>
    </>
  );
};
